/* eslint-disable no-console */
import { Box, Skeleton } from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import { CreditAgreement, typeguards } from '@finley-technologies/finley-types';

import {
  SettingsFacilityBorrowingBaseTabOptionKeys,
  SettingsFacilityBorrowingBaseTemplate,
} from '../SettingsFacilityBorrowingBaseTemplate';
import { BaseFieldsWipStickyHeader } from './BaseFieldsWipStickyHeader';
import { LoanProductBaseFieldConfig } from './LoanProductBaseFieldConfig';
import {
  GetLoanTapeSanitizeConfigsQueryQueryGQLType,
  useGetLoanTapeSanitizeConfigsQueryQuery,
} from './requests/requests.gql.generated';
import { LoanProductSelect } from 'packages/common-frontend/components/LoanProductSelect';

const getTapeSanitizeConfig = ({
  loanProductSlug,
  data,
}: {
  loanProductSlug: string;
  data: GetLoanTapeSanitizeConfigsQueryQueryGQLType | undefined;
}): CreditAgreement.DataTapeField.LoanTapeSanitizeConfig | null => {
  const matchingConfig = data?.getLoanTapeSanitizeConfigs?.find((config) => {
    return config.loanProductSlug === loanProductSlug;
  });

  if (!matchingConfig) {
    return null;
  }

  const {
    baseFields: baseFieldsString,
    rawExternalReceivableIdField,
    sanitizeArtifactSlug,
  } = matchingConfig;

  let baseFields: CreditAgreement.DataTapeField.BaseConfig[] = [];
  try {
    const parsed = JSON.parse(baseFieldsString);
    if (typeguards.creditAgreement.isBaseFieldConfigArray(parsed)) {
      baseFields = parsed;
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error parsing baseFieldConfigs:', e);
  }

  return {
    loanProductSlug,
    rawExternalReceivableIdField,
    sanitizeArtifactSlug,
    baseFields,
  };
};

export const LoanTapeDataView: React.FC<{
  creditFacilityId: string;
  data: GetLoanTapeSanitizeConfigsQueryQueryGQLType;
}> = ({ creditFacilityId, data }) => {
  const [currentLoanProductId, setCurrentLoanProductId] = useState<
    string | null
  >(data.creditFacility.loanProducts[0].id);

  // this maintain current config (including any changes made but not saved)
  const [
    currentTapeSanitizationConfig,
    setCurrentTapeSanitizationConfig,
  ] = React.useState<CreditAgreement.DataTapeField.LoanTapeSanitizeConfig | null>(
    null,
  );

  // dirty is set to true anytime current config changes
  const [dirty, setDirty] = React.useState(false);

  // this maintains the original config (before any changes were made)
  const originalTpeSanitizationConfig = useMemo(() => {
    const loanProductSlug = data.creditFacility.loanProducts.find(
      (product) => product.id === currentLoanProductId,
    )?.slug;
    if (!loanProductSlug) {
      return null;
    }
    return getTapeSanitizeConfig({
      loanProductSlug,
      data,
    });
  }, [currentLoanProductId, data]);

  // anytime original config changes update current config
  React.useEffect(() => {
    setCurrentTapeSanitizationConfig(originalTpeSanitizationConfig);
    setDirty(false);
  }, [originalTpeSanitizationConfig]);

  return (
    <Box overflowY="auto" maxH="100vh">
      <BaseFieldsWipStickyHeader
        oldValue={originalTpeSanitizationConfig}
        newValue={currentTapeSanitizationConfig}
        isCommitDisabled={dirty} // when WIP dirty, don't allow commit, as they need to save WIP first.
        isSaveDisabled={!dirty}
        creditFacilityId={creditFacilityId}
        errors={[]}
        borrowerId={data.creditFacility.borrowerId}
        operationMode="lender"
      />

      <SettingsFacilityBorrowingBaseTemplate
        creditFacilityId={creditFacilityId}
        selectedTab={SettingsFacilityBorrowingBaseTabOptionKeys.loantape}
      >
        <Box mt="20px">
          <Box>
            <LoanProductSelect
              creditFacilityId={creditFacilityId}
              loanProductId={currentLoanProductId}
              setLoanProductId={setCurrentLoanProductId}
              isDisabled={dirty} // when dirty, we cannot allow change of loan product
            />
          </Box>
          {currentTapeSanitizationConfig && (
            <LoanProductBaseFieldConfig
              baseFields={currentTapeSanitizationConfig.baseFields}
              onUpdate={(baseFields) => {
                setDirty(true);
                setCurrentTapeSanitizationConfig({
                  ...currentTapeSanitizationConfig,
                  baseFields,
                });
              }}
            />
          )}
          {!currentTapeSanitizationConfig && <Box>No base fields found</Box>}
        </Box>
      </SettingsFacilityBorrowingBaseTemplate>
    </Box>
  );
};

export const LoanTapeView: React.FC<{
  creditFacilityId: string;
}> = ({ creditFacilityId }) => {
  const { data } = useGetLoanTapeSanitizeConfigsQueryQuery({
    variables: {
      creditFacilityId,
    },
  });

  if (!data?.creditFacility || !data?.getLoanTapeSanitizeConfigs) {
    return <Skeleton />;
  }

  const {
    creditFacility: { loanProducts },
  } = data;

  if (loanProducts.length === 0) {
    return <Box>No loan products found</Box>;
  }

  return <LoanTapeDataView creditFacilityId={creditFacilityId} data={data} />;
};
